import { useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import CategoryTabs from '@/components/base/CategoryTabs';
import { getComicsByCategory } from '@/mocks/comics';
import { SITE_NAME, CATEGORIES } from '@/mocks/site';

const COMICS_PER_PAGE = 12;

export default function CategoryPage() {
  const { category = 'all' } = useParams<{ category: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<'latest' | 'hot' | 'rating'>('latest');
  const [openSort, setOpenSort] = useState(false);

  const categoryLabel = CATEGORIES.find((c) => c.key === category)?.label || '全部分类';

  const filteredComics = useMemo(() => {
    let list = getComicsByCategory(category);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.author.toLowerCase().includes(q) ||
          c.tags.some((t) => t.toLowerCase().includes(q)),
      );
    }
    if (sortBy === 'latest') {
      list = [...list].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    } else if (sortBy === 'hot') {
      list = [...list].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      list = [...list].sort((a, b) => b.rating - a.rating);
    }
    return list;
  }, [category, searchQuery, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredComics.length / COMICS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pagedComics = filteredComics.slice((safePage - 1) * COMICS_PER_PAGE, safePage * COMICS_PER_PAGE);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryLabel} - ${SITE_NAME}`,
    description: `汗汗漫画 ${categoryLabel} 分类，提供最全下拉式韩漫免费在线阅读`,
    url: `https://hanhancomic.com/category/${category}`,
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: 'https://hanhancomic.com/',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <div className="min-h-screen bg-background-50 pt-16">
        <Navbar />

        <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '分类' },
              { label: categoryLabel },
            ]}
          />

          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
              {searchQuery ? `搜索"${searchQuery}"的结果` : categoryLabel}
            </h1>
            {searchQuery && (
              <p className="text-sm text-foreground-500 mt-1">共找到 {filteredComics.length} 部相关漫画</p>
            )}
          </div>

          {/* Category Tabs */}
          <div className="mb-6">
            <CategoryTabs active={category} />
          </div>

          {/* Sort Bar */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-foreground-500">共 {filteredComics.length} 部作品</p>
            <div className="relative">
              <button
                onClick={() => setOpenSort(!openSort)}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-background-100 border border-background-200/70 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-sort-desc text-base"></i>
                {sortBy === 'latest' ? '最新更新' : sortBy === 'hot' ? '最热阅读' : '评分最高'}
                <i className={`ri-arrow-down-s-line transition-transform ${openSort ? 'rotate-180' : ''}`}></i>
              </button>
              {openSort && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setOpenSort(false)}></div>
                  <div className="absolute right-0 top-full mt-1 z-20 bg-background-50 border border-background-200/70 rounded-lg shadow-sm py-1 min-w-[140px]">
                    {[
                      { value: 'latest', label: '最新更新' },
                      { value: 'hot', label: '最热阅读' },
                      { value: 'rating', label: '评分最高' },
                    ].map((s) => (
                      <button
                        key={s.value}
                        onClick={() => {
                          setSortBy(s.value as typeof sortBy);
                          setCurrentPage(1);
                          setOpenSort(false);
                        }}
                        className={`block w-full text-left px-4 py-2 text-sm hover:bg-background-100 transition-colors cursor-pointer ${
                          sortBy === s.value ? 'text-primary-600 font-medium' : 'text-foreground-700'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Comic Grid */}
          {pagedComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5 mb-10">
              {pagedComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} showCategory />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-600 text-lg mb-2">没有找到相关漫画</p>
              <p className="text-sm text-foreground-500">试试其他搜索词或浏览其他分类吧</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pb-10">
              <button
                onClick={() => setCurrentPage(Math.max(1, safePage - 1))}
                disabled={safePage <= 1}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 border border-background-200/70 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-medium transition-colors cursor-pointer ${
                    p === safePage
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 border border-background-200/70 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, safePage + 1))}
                disabled={safePage >= totalPages}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 border border-background-200/70 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}